import {
  Search,
  ChevronDown,
  Bell,
  ShoppingCart,
  LogOut,
  UserCircle,
  Plus,
  Sun,
  Moon,
  Globe,
  MessageSquare,
  Menu,
  X,
  MapPin,
  Briefcase,
  Users,
  FileText,
  Building,
  Heart,
  Bookmark,
  Settings,
  BarChart,
  List,
  Phone,
  Home,
  Send,
  Award,
  TrendingUp,
  UserCheck,
  Store,
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useLogout } from '@/hooks/useAuth';
import { useUIStore } from '@/store/uiStore';
import { can, hasRole } from '@/utils/permissions';
import { getInitials } from '@/utils';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Select } from '@/components/ui/select';
import { useProvinces } from '@/hooks/useAddress';
import { useEffect, useRef, useState } from 'react';
import NotificationPanel from '@/components/chat/NotificationPanel';

const CATEGORIES = [
  { label: 'Tất cả', value: '' },
  { label: 'Sang nhượng', value: 'sang-nhuong' },
  { label: 'Cho thuê', value: 'cho-thue' },
  { label: 'Việc làm', value: 'viec-lam' },
  { label: 'Nhượng quyền', value: 'nhuong-quyen' },
];

interface NavSubItem {
  label: string;
  to: string;
  icon?: React.ReactNode;
}

interface NavGroup {
  title: string;
  items: NavSubItem[];
  show?: (user: any) => boolean;
}

interface NavItemType {
  label: string;
  to: string;
  groups?: NavGroup[];
}

const NAV_ITEMS: NavItemType[] = [
  {
    label: 'Sang nhượng',
    to: '/sang-nhuong',
    groups: [
      {
        title: 'Bên cho sang nhượng',
        // show: (user) => can.createTransfer(user),
        items: [
          { label: 'Đăng tin sang nhượng', to: '/sang-nhuong/tao-tin', icon: <Plus size={16} /> },
          { label: 'Quản lý sang nhượng', to: '/sang-nhuong/quan-ly', icon: <Store size={16} /> },
          { label: 'Danh sách nhu cầu sang nhượng', to: '/nhu-cau-sang-nhuong', icon: <List size={16} /> },
          { label: 'Tin nhu cầu sang nhượng đã lưu', to: '/nhu-cau-sang-nhuong/da-luu', icon: <Heart size={16} /> },
          { label: 'Tin nhu cầu sang nhượng đã đề nghị', to: '/nhu-cau-sang-nhuong/da-de-nghi', icon: <FileText size={16} /> },
        ],
      },
      {
        title: 'Bên nhận sang nhượng',
        // show: (user) => hasRole(user, 'restaurantbuyer', 'admin'),
        items: [
          { label: 'Đăng nhu cầu sang nhượng', to: '/nhu-cau-sang-nhuong/tao-tin', icon: <Plus size={16} /> },
          // { label: 'Quản lý nhu cầu sang nhượng', to: '/user/tin-dang', icon: <Store size={16} /> },
          { label: 'Danh sách tin sang nhượng', to: '/sang-nhuong', icon: <List size={16} /> },
          { label: 'Tin sang nhượng đã lưu', to: '/sang-nhuong/da-luu', icon: <Heart size={16} /> },
          { label: 'Tin sang nhượng đã đề nghị', to: '/sang-nhuong/da-de-nghi', icon: <FileText size={16} /> },
        ],
      },
    ],
  },
  {
    label: 'Cho thuê',
    to: '/cho-thue',
    groups: [
      {
        title: 'Bên cho thuê',
        // show: (user) => can.createRental(user),
        items: [
          { label: 'Đăng tin cho thuê', to: '/cho-thue/tao-tin', icon: <Plus size={16} /> },
          { label: 'Danh sách nhu cầu cho thuê', to: '/nhu-cau-cho-thue', icon: <List size={16} /> },
          { label: 'Tin nhu cầu cho thuê đã lưu', to: '/nhu-cau-cho-thue/da-luu', icon: <Heart size={16} /> },
          { label: 'Tin nhu cầu cho thuê đã đề nghị', to: '/nhu-cau-cho-thue/da-de-nghi', icon: <FileText size={16} /> },
        ],
      },
      {
        title: 'Bên nhận thuê',
        // show: (user) => hasRole(user, 'propertyseeker', 'admin'),
        items: [
          { label: 'Đăng nhu cầu cho thuê', to: '/nhu-cau-cho-thue/tao-tin', icon: <Plus size={16} /> },
          { label: 'Danh sách tin cho thuê', to: '/cho-thue', icon: <List size={16} /> },
          { label: 'Tin cho thuê đã lưu', to: '/cho-thue/da-luu', icon: <Heart size={16} /> },
          { label: 'Tin cho thuê đã đề nghị', to: '/cho-thue/da-de-nghi', icon: <FileText size={16} /> },
        ],
      },
    ],
  },
  {
    label: 'Việc làm',
    to: '/viec-lam',
    groups: [
      {
        title: 'Nhà tuyển dụng',
        // show: (user) => can.createJob(user),
        items: [
          { label: 'Đăng tin tuyển dụng', to: '/viec-lam/dang-tuyen', icon: <Plus size={16} /> },
          { label: 'Quản lý tuyển dụng', to: '/viec-lam/quan-ly', icon: <Briefcase size={16} /> },
          // { label: 'Ứng viên đã liên hệ', to: '/user/contacted-candidates', icon: <Users size={16} /> },
          // { label: 'Ứng viên đã lưu', to: '/user/saved-candidates', icon: <UserCheck size={16} /> },
        ],
      },
      {
        title: 'Người tìm việc',
        // show: (user) => hasRole(user, 'jobseeker', 'admin'),
        items: [
          { label: 'Tìm việc làm', to: '/viec-lam', icon: <Search size={16} /> },
          { label: 'Việc làm đã lưu', to: '/viec-lam/da-luu', icon: <Heart size={16} /> },
          { label: 'Việc làm đã ứng tuyển', to: '/viec-lam/da-ung-tuyen', icon: <FileText size={16} /> },
          { label: 'Danh sách công ty', to: '/cong-ty', icon: <List size={16} /> },
        ],
      },
    ],
  },
  {
    label: 'Nhượng quyền',
    to: '/nhuong-quyen',
    groups: [
      {
        title: 'Bên cho nhượng quyền',
        // show: (user) => can.createFranchise(user),
        items: [
          { label: 'Đăng tin nhượng quyền', to: '/nhuong-quyen/tao-tin', icon: <Plus size={16} /> },
          { label: 'Danh sách nhu cầu nhượng quyền', to: '/nhu-cau-nhuong-quyen', icon: <List size={16} /> },
          { label: 'Tin nhu cầu nhượng quyền đã lưu', to: '/nhu-cau-nhuong-quyen/da-luu', icon: <Heart size={16} /> },
          { label: 'Tin nhu cầu nhượng quyền đã đề nghị', to: '/nhu-cau-nhuong-quyen/da-de-nghi', icon: <FileText size={16} /> },
        ],
      },
      {
        title: 'Bên nhận nhượng quyền',
        // show: (user) => hasRole(user, 'franchiseseeker', 'admin'),
        items: [
          { label: 'Đăng nhu cầu nhượng quyền', to: '/nhu-cau-nhuong-quyen/tao-tin', icon: <Plus size={16} /> },
          { label: 'Danh sách tin nhượng quyền', to: '/nhuong-quyen', icon: <List size={16} /> },
          { label: 'Tin nhượng quyền đã lưu', to: '/nhuong-quyen/da-luu', icon: <Heart size={16} /> },
          { label: 'Tin nhượng quyền đã đề nghị', to: '/nhuong-quyen/da-de-nghi', icon: <FileText size={16} /> },
        ],
      },
    ],
  },
];

export default function Navbar() {
  const { isAuthenticated, user } = useAuthStore();

  const logout = useLogout();
  const navigate = useNavigate();
  const { theme, toggleTheme, setSearchQuery, setProvinceFilter, searchQuery, provinceFilter } = useUIStore();

  const location = useLocation();
  const isHome = location.pathname === '/';

  const [search, setSearch] = useState('');
  const [catOpen, setCatOpen] = useState(false);
  const [selCat, setSelCat] = useState(CATEGORIES[0]);
  const [dropOpen, setDropOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const isDark = theme === 'dark';
  const [selectedProvince, setSelectedProvince] = useState<string>('');

  const { data: provinces = [], isLoading: loadingProvinces } = useProvinces(1);

  const toOpts = (arr: { id: number; name: string }[]) => arr.map((i) => ({ value: String(i.id), label: i.name }));

  // Close dropdowns on outside click
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setDropOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleTheme = () => {
    setSpinning(true);
    toggleTheme();
  };
  useEffect(() => {
    if (!spinning) return;
    const t = setTimeout(() => setSpinning(false), 420);
    return () => clearTimeout(t);
  }, [spinning]);

  const handleSearch = () => {
    const q = search.trim();
    setSearchQuery(q);

    const isHomePage = location.pathname === '/';
    if (!isHomePage) {
      const base = selCat.value || 'sang-nhuong';
      navigate(`/${base}?q=${encodeURIComponent(q)}`);
    }
    // Nếu ở Home → store đã set, HomePage tự re-render
  };

  const handleClear = () => {
    setSearch('');
    setSearchQuery('');
    setSelectedProvince('');
    setProvinceFilter(undefined);
  };

  const handleProvinceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setSelectedProvince(val);
    setProvinceFilter(val ? Number(val) : undefined);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full">
      {/* ══════ TOP BAR (dark) ══════ */}
      <div className="w-full" style={{ background: 'var(--navbar-top)' }}>
        <div className="max-w-[1280px] mx-auto px-4 h-14 flex items-center justify-between gap-3">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0 group mr-2">
            <img src="/logo.png" alt="FNB HUB" className="h-9 w-9 rounded-lg object-cover" />
            <span className="font-display font-black text-lg text-white tracking-tight">
              FNB <span style={{ color: 'var(--brand)' }}>HUB</span>
            </span>
          </Link>

          {/* ── Search bar (Hidden on small mobile screens to prevent overflow) ── */}
          <div className="hidden md:flex flex-1 mx-2">
            {isAuthenticated && user && isHome && (
              <div className="fnb-search max-w-[720px] mx-auto h-10 w-full">
                {/* Province select */}
                <div className="flex items-center border-r shrink-0" style={{ borderColor: 'var(--border-c)' }}>
                  <MapPin size={14} className="ml-3 shrink-0" style={{ color: 'var(--text-3)' }} />
                  <select
                    value={selectedProvince}
                    onChange={handleProvinceChange}
                    disabled={loadingProvinces}
                    className="h-full pl-2 pr-7 text-sm bg-transparent outline-none cursor-pointer appearance-none"
                    style={{
                      color: selectedProvince ? 'var(--text-1)' : 'var(--text-3)',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239CA3AF' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 6px center',
                      minWidth: 120,
                      maxWidth: 160,
                    }}
                  >
                    <option value="">Tất cả</option>
                    {provinces.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Search input */}
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Bạn đang tìm gì?"
                  className="flex-1 px-4 text-sm bg-transparent outline-none min-w-0"
                  style={{ color: 'var(--text-1)' }}
                />

                {/* Clear button */}
                {(search || selectedProvince) && (
                  <button onClick={handleClear} className="w-8 h-full flex items-center justify-center shrink-0 hover:opacity-70 transition-opacity" title="Xóa tìm kiếm">
                    <X size={14} style={{ color: 'var(--text-3)' }} />
                  </button>
                )}

                {/* Search button */}
                <button
                  onClick={handleSearch}
                  className="flex items-center justify-center w-12 h-full shrink-0 transition-opacity hover:opacity-90"
                  style={{ background: 'var(--brand)' }}
                >
                  <Search size={17} className="text-white" />
                </button>
              </div>
            )}
          </div>

          {/* Right icons */}
          <div className="hidden md:flex items-center gap-1 shrink-0">
            {/* Theme */}
            {/* <button onClick={handleTheme} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all">
              {isDark ? <Sun size={16} className={spinning ? 'animate-spin' : ''} /> : <Moon size={16} className={spinning ? 'animate-spin' : ''} />}
            </button> */}

            {/* Lang */}
            {/* <button className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-gray-300 hover:text-white hover:bg-white/10 transition-all">
              🇻🇳 VI <ChevronDown size={11} />
            </button> */}

            {isAuthenticated && user ? (
              <>
                <Link to="/tin-nhan" className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all">
                  <MessageSquare size={16} />
                </Link>
                <NotificationPanel />
                <button className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-white/10">
                  <ShoppingCart size={16} />
                </button>

                {/* User avatar dropdown */}
                <div className="relative ml-1" ref={dropRef}>
                  <button onClick={() => setDropOpen((v) => !v)} className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-xl hover:bg-white/10 transition-all">
                    <Avatar className="h-7 w-7 rounded-lg">
                      <AvatarFallback className="rounded-lg text-xs bg-orange-500 text-white font-bold">{getInitials(user.fullName || user.username)}</AvatarFallback>
                    </Avatar>
                    <ChevronDown size={12} className={cn('text-gray-400 transition-transform', dropOpen && 'rotate-180')} />
                  </button>

                  {dropOpen && (
                    <div className="absolute right-0 top-full mt-2 w-56 rounded-xl border bg-white shadow-xl z-50 overflow-hidden animate-slide-down">
                      <div className="px-4 py-3 border-b">
                        <p className="text-sm font-semibold text-gray-800 truncate">{user.fullName || user.username}</p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        <Badge className="mt-1.5 text-xs bg-orange-100 text-orange-600 hover:bg-orange-100">{user.roles?.[0]?.roleName ?? user.role}</Badge>
                      </div>
                      <div className="p-1.5">
                        <DropLink to="/ho-so" icon={<UserCircle size={14} />} label="Thông tin tài khoản" onClick={() => setDropOpen(false)} />
                        <DropLink to="/ho-so-ung-vien" icon={<FileText size={14} />} label="Hồ sơ cá nhân" onClick={() => setDropOpen(false)} />
                        <div className="my-1 h-px bg-gray-100" />
                        <button
                          onClick={() => {
                            setDropOpen(false);
                            logout();
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-500 hover:bg-red-50 transition-colors"
                        >
                          <LogOut size={14} /> Đăng xuất
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 ml-1">
                <Link to="/login" className="px-3 py-1.5 text-sm text-gray-300 hover:text-white transition-colors">
                  Đăng nhập
                </Link>
                <Link to="/register" className="px-3 py-1.5 text-sm font-semibold rounded-lg text-white transition-all hover:opacity-90" style={{ background: 'var(--brand)' }}>
                  Đăng ký
                </Link>
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg text-gray-300 hover:bg-white/10" onClick={() => setMobileOpen((v) => !v)}>
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* ══════ SECONDARY NAV (white) ══════ */}
      <div
        className="hidden md:block w-full border-b"
        style={{
          background: 'var(--navbar-bg)',
          borderColor: 'var(--border-c)',
        }}
      >
        <div className="max-w-[1280px] mx-auto px-4">
          <nav className="flex items-center gap-2 h-12">
            {NAV_ITEMS.map((item) => (
              <NavDropdown key={item.label} item={item} />
            ))}
          </nav>
        </div>
      </div>

      {/* ══════ MOBILE MENU ══════ */}
      {mobileOpen && (
        <div
          className="fixed inset-x-0 top-14 bottom-0 z-50 overflow-y-auto border-b shadow-2xl animate-slide-down md:hidden"
          style={{
            background: 'var(--surface-0)',
            borderColor: 'var(--border-c)',
          }}
        >
          {/* Mobile search */}
          <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border-c)' }}>
            <div className="fnb-search h-10">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                    setMobileOpen(false);
                  }
                }}
                placeholder="Bạn đang tìm gì?"
                className="flex-1 px-4 text-sm bg-transparent outline-none"
              />
              <button
                onClick={() => {
                  handleSearch();
                  setMobileOpen(false);
                }}
                className="w-12 h-full flex items-center justify-center"
                style={{ background: 'var(--brand)' }}
              >
                <Search size={16} className="text-white" />
              </button>
            </div>
          </div>

          <nav className="py-1">
            {NAV_ITEMS.map((item) => (
              <div key={item.label}>
                <NavLink
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    cn('flex px-5 py-3 text-sm font-bold transition-colors', isActive ? 'text-orange-500 bg-orange-50' : 'text-gray-900 hover:bg-gray-50')
                  }
                >
                  {item.label}
                </NavLink>
                {item.groups && (
                  <div className="bg-gray-50/50 pb-2">
                    {item.groups
                      .filter((g) => !g.show || g.show(user))
                      .map((group) => (
                        <div key={group.title} className="px-6 py-2">
                          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{group.title}</div>
                          <div className="flex flex-col gap-0.5">
                            {group.items.map((sub) => (
                              <Link
                                key={sub.label}
                                to={sub.to}
                                onClick={() => setMobileOpen(false)}
                                className="py-2 text-[14px] text-gray-700 hover:text-orange-600 transition-colors flex items-center gap-3 font-medium"
                              >
                                <span className="text-gray-400 group-hover:text-orange-500 shrink-0">{sub.icon}</span>
                                <span className="truncate">{sub.label}</span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="px-4 py-3 border-t flex items-center justify-between" style={{ borderColor: 'var(--border-c)' }}>
            {/* <button onClick={handleTheme} className="flex items-center gap-2 text-sm text-gray-600">
              {isDark ? <Sun size={15} /> : <Moon size={15} />}
              {isDark ? 'Chế độ sáng' : 'Chế độ tối'}
            </button> */}
            {!isAuthenticated && (
              <div className="flex gap-2">
                <Link to="/login" onClick={() => setMobileOpen(false)} className="px-3 py-1.5 text-sm text-gray-600 border rounded-lg">
                  Đăng nhập
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-1.5 text-sm font-semibold text-white rounded-lg"
                  style={{ background: 'var(--brand)' }}
                >
                  Đăng ký
                </Link>
              </div>
            )}
            {isAuthenticated && user && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">{user.firstName || user.username}</span>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    logout();
                  }}
                  className="text-sm text-red-500"
                >
                  Đăng xuất
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function DropLink({ to, icon, label, onClick }: { to: string; icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <Link to={to} onClick={onClick} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors">
      <span className="text-gray-400">{icon}</span>
      {label}
    </Link>
  );
}

function NavDropdown({ item }: { item: NavItemType }) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuthStore();
  const isActive = location.pathname.startsWith(item.to);

  const visibleGroups = item.groups?.filter((g) => !g.show || g.show(user)) || [];

  if (visibleGroups.length === 0 && !item.groups) {
    return (
      <NavLink
        to={item.to}
        className={cn(
          'px-4 h-full flex items-center text-[14px] font-semibold border-b-[3px] transition-all outline-none',
          isActive ? 'border-orange-500 text-orange-500' : 'border-transparent text-gray-700 hover:text-orange-600 hover:border-orange-200',
        )}
      >
        {item.label}
      </NavLink>
    );
  }

  return (
    <div className="relative h-full" onMouseEnter={() => setIsOpen(true)} onMouseLeave={() => setIsOpen(false)}>
      <NavLink
        to={item.to}
        className={cn(
          'px-4 h-full flex items-center text-[14px] font-semibold border-b-[3px] transition-all gap-1.5 outline-none',
          isActive ? 'border-orange-500 text-orange-500' : 'border-transparent text-gray-700 hover:text-orange-600 hover:border-orange-200',
        )}
      >
        {item.label}
        {visibleGroups.length > 0 && <ChevronDown size={14} className={cn('transition-transform duration-300 opacity-40', isOpen && 'rotate-180 opacity-100 text-orange-500')} />}
      </NavLink>

      {visibleGroups.length > 0 && isOpen && (
        <div className="absolute top-[calc(100%-1px)] left-0 pt-2 z-[100] animate-slide-down">
          <div className="bg-white border border-gray-100 rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.15)] p-4 flex flex-col gap-5 min-w-[260px] backdrop-blur-xl">
            {visibleGroups.map((group) => (
              <div key={group.title} className="flex flex-col">
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-1.5 px-2">{group.title}</h4>
                <div className="flex flex-col space-y-0.5">
                  {group.items.map((subItem) => (
                    <Link
                      key={subItem.label}
                      to={subItem.to}
                      className="group/sub flex items-center gap-3 p-2 px-2.5 rounded-lg hover:bg-orange-50/80 transition-all active:scale-[0.97]"
                    >
                      <span className="text-gray-400 group-hover/sub:text-orange-500 transition-colors shrink-0">{subItem.icon}</span>
                      <span className="text-[14px] font-bold text-gray-700 group-hover/sub:text-orange-600 transition-colors whitespace-nowrap">{subItem.label}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
